import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ExpiringCredentialItem } from '@dsr/shared-types';
import { useExpiringCredentials } from '../api/queries/personnel';
import { ApiError } from '../api/client';
import { Badge, Select, Table, type BadgeTone, type SelectOption } from '../components/ui';

// The dashboard always pulls the 90-day horizon in one request, then buckets
// client-side into <=30 / <=60 / <=90 day windows. This keeps the server call
// to a single round-trip while letting the user pivot the horizon filter
// without refetching.

const DAY_MS = 24 * 60 * 60 * 1000;
const HORIZON_DAYS = 90;

type Bucket = '30' | '60' | '90' | 'past';

const KIND_LABEL: Record<ExpiringCredentialItem['kind'], string> = {
  license: 'License',
  certification: 'Certification',
  clearance: 'Clearance',
  firearms_qualification: 'Firearms Qual',
};

function bucketFor(expiresAt: number, now: number): Bucket {
  const days = (expiresAt - now) / DAY_MS;
  if (days < 0) return 'past';
  if (days <= 30) return '30';
  if (days <= 60) return '60';
  return '90';
}

function bucketTone(bucket: Bucket): 'danger' | 'warn' | 'default' {
  if (bucket === 'past' || bucket === '30') return 'danger';
  if (bucket === '60') return 'warn';
  return 'default';
}

// The countdown pill maps the bucket tone onto a shared Badge tone (the
// row-class tone keeps its own 'default' string for .expiry-* row coloring).
const BADGE_TONE: Record<'danger' | 'warn' | 'default', BadgeTone> = {
  danger: 'danger',
  warn: 'warn',
  default: 'neutral',
};

function fmtDate(epoch: number): string {
  const d = new Date(epoch);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function daysUntil(epoch: number, now: number): string {
  const days = Math.round((epoch - now) / DAY_MS);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  return `${days}d`;
}

export function CredentialExpiryPage(): JSX.Element {
  const { data, isLoading, isError, error } = useExpiringCredentials(HORIZON_DAYS);
  const now = Date.now();

  // Filter pivot: which horizon window to show. 'all' shows everything <=90d.
  const [horizon, setHorizon] = useState<'30' | '60' | '90'>('90');
  // 'all' = no type filter (Radix Select forbids an empty value).
  const [kind, setKind] = useState<ExpiringCredentialItem['kind'] | 'all'>('all');

  const items = useMemo(() => data?.expiring ?? [], [data]);

  const counts = useMemo(() => {
    const c = { past: 0, '30': 0, '60': 0, '90': 0 };
    for (const it of items) c[bucketFor(it.expires_at, now)]++;
    return c;
  }, [items, now]);

  const horizonLimitDays = Number(horizon);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (kind !== 'all' && it.kind !== kind) return false;
      const days = (it.expires_at - now) / DAY_MS;
      // Always include already-expired items regardless of horizon — they're
      // the most urgent.
      return days <= horizonLimitDays;
    });
  }, [items, kind, horizonLimitDays, now]);

  const horizonOptions: SelectOption[] = [
    { value: '30', label: 'Within 30 days' },
    { value: '60', label: 'Within 60 days' },
    { value: '90', label: 'Within 90 days' },
  ];
  const kindOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    { value: 'license', label: 'Licenses' },
    { value: 'certification', label: 'Certifications' },
    { value: 'clearance', label: 'Clearances' },
    { value: 'firearms_qualification', label: 'Firearms Quals' },
  ];

  return (
    <section className="page credential-expiry-page">
      <header className="page-header">
        <div>
          <h1>Credential Expirations</h1>
          <p className="page-sub">
            Licenses, certifications, clearances, and firearms qualifications
            expiring within {HORIZON_DAYS} days.
          </p>
        </div>
      </header>

      <section className="kpi-grid">
        <ExpiryKpi
          label="Expired / ≤ 30 days"
          value={counts.past + counts['30']}
          tone="danger"
          active={horizon === '30'}
          onClick={() => setHorizon('30')}
        />
        <ExpiryKpi
          label="≤ 60 days"
          value={counts.past + counts['30'] + counts['60']}
          tone="warn"
          active={horizon === '60'}
          onClick={() => setHorizon('60')}
        />
        <ExpiryKpi
          label="≤ 90 days"
          value={counts.past + counts['30'] + counts['60'] + counts['90']}
          tone="default"
          active={horizon === '90'}
          onClick={() => setHorizon('90')}
        />
      </section>

      <div className="filters-bar">
        <Select
          value={horizon}
          onValueChange={(v) => setHorizon(v as '30' | '60' | '90')}
          options={horizonOptions}
          aria-label="Filter by horizon"
        />
        <Select
          value={kind}
          onValueChange={(v) => setKind(v as ExpiringCredentialItem['kind'] | 'all')}
          options={kindOptions}
          aria-label="Filter by type"
        />
      </div>

      {isLoading && <div className="page-state">Loading credential expirations…</div>}
      {isError && (
        <div className="page-state page-error" role="alert">
          {error instanceof ApiError ? error.message : 'Failed to load expirations.'}
        </div>
      )}

      {!isLoading && !isError && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Personnel</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Credential</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Countdown</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {filtered.length === 0 && (
              <Table.Empty colSpan={5}>Nothing expiring in this window. 🎉</Table.Empty>
            )}
            {filtered.map((it) => {
              const b = bucketFor(it.expires_at, now);
              return (
                <Table.Row key={`${it.kind}-${it.id}`} className={`expiry-row expiry-${bucketTone(b)}`}>
                  <Table.Cell tone="strong">
                    <Link to={`/personnel/${it.personnel_id}`} className="table-link">
                      {it.personnel_legal_name}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{KIND_LABEL[it.kind]}</Table.Cell>
                  <Table.Cell className="capitalize">{it.label.replace(/_/g, ' ')}</Table.Cell>
                  <Table.Cell>{fmtDate(it.expires_at)}</Table.Cell>
                  <Table.Cell>
                    <Badge tone={BADGE_TONE[bucketTone(b)]}>
                      {daysUntil(it.expires_at, now)}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </section>
  );
}

interface ExpiryKpiProps {
  label: string;
  value: number;
  tone: 'danger' | 'warn' | 'default';
  active: boolean;
  onClick: () => void;
}

function ExpiryKpi({ label, value, tone, active, onClick }: ExpiryKpiProps): JSX.Element {
  return (
    <button
      type="button"
      className={`kpi-card kpi-card-button ${active ? 'active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value kpi-${tone}`}>{value}</div>
      <div className="kpi-meta">cumulative</div>
    </button>
  );
}
